import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { Icon } from '@/components/Icon'
import { useReservationsAddon } from '@/hooks/useReservationsAddon'
import { WeekCalendar } from './WeekCalendar'
import { MonthCalendar } from './MonthCalendar'

interface Reservation {
  id: string
  reference: string
  customer_name: string
  customer_email?: string
  customer_phone?: string
  reservation_date: string
  reservation_time: string
  party_size: number
  resource_id?: string
  resource_name?: string
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed' | 'no_show'
  notes?: string
  source: string
  conversation_id?: string
  created_at: string
}

const STATUSES = ['pending', 'confirmed', 'cancelled', 'completed', 'no_show'] as const

const normalizeDate = (value: string) => String(value).slice(0, 10)
const normalizeTime = (value: string) => String(value).slice(0, 5)

export const Reservations: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { visible, isLoading: addonLoading } = useReservationsAddon()

  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const [view, setView] = useState<'list' | 'week' | 'month'>('list')
  const [weekFocusDate, setWeekFocusDate] = useState<string | null>(null)
  const [actionError, setActionError] = useState('')
  const limit = 20

  const { data, isLoading } = useQuery({
    queryKey: ['reservations', page, search, statusFilter, dateFrom, dateTo],
    queryFn: async () => {
      const params = new URLSearchParams()
      params.append('limit', String(limit))
      params.append('offset', String((page - 1) * limit))
      if (search) params.append('search', search)
      if (statusFilter) params.append('status', statusFilter)
      if (dateFrom) params.append('dateFrom', dateFrom)
      if (dateTo) params.append('dateTo', dateTo)
      const response = await api.get(`/reservations?${params.toString()}`)
      return response.data
    },
    enabled: visible && view === 'list',
  })

  const reservations: Reservation[] = data?.data || []
  const total: number = data?.meta?.total || 0
  const totalPages = Math.max(1, Math.ceil(total / limit))

  const changeStatus = async (id: string, status: string) => {
    try {
      setActionError('')
      await api.patch(`/reservations/${id}/status`, { status })
      queryClient.invalidateQueries({ queryKey: ['reservations'] })
    } catch (err: any) {
      setActionError(err.response?.data?.error || t('reservations.actionError'))
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500/20 text-yellow-400'
      case 'confirmed': return 'bg-green-500/20 text-green-400'
      case 'cancelled': return 'bg-red-500/20 text-red-400'
      case 'completed': return 'bg-blue-500/20 text-blue-400'
      case 'no_show': return 'bg-orange-500/20 text-orange-400'
      default: return 'bg-gray-500/20 text-gray-400'
    }
  }

  if (!addonLoading && !visible) {
    return (
      <div className="bg-[#12121a] border border-white/10 rounded-xl p-8 text-center">
        <Icon name="lock" className="text-3xl text-gray-500 mb-4" />
        <h1 className="text-xl font-bold mb-2">{t('reservations.notAvailableTitle')}</h1>
        <p className="text-gray-400">{t('reservations.notAvailableDesc')}</p>
      </div>
    )
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <h1 className="text-3xl font-bold">{t('reservations.title')}</h1>
        <div className="flex gap-2">
          <button
            onClick={() => navigate('/reservations/settings')}
            className="px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg font-medium transition-colors flex items-center gap-2"
          >
            <Icon name="gear" className="w-4" />
            {t('reservations.settingsButton')}
          </button>
          <button
            onClick={() => setShowCreate(true)}
            className="px-4 py-2 bg-[#006cd4] hover:bg-[#006cd4]/80 rounded-lg font-medium transition-colors flex items-center gap-2"
          >
            <Icon name="plus" className="w-4" />
            {t('reservations.newReservation')}
          </button>
        </div>
      </div>

      {/* View toggle */}
      <div className="flex gap-1 mb-6 bg-[#12121a] border border-white/10 rounded-lg p-1 w-fit">
        <button
          onClick={() => setView('list')}
          className={`px-4 py-1.5 rounded-md text-sm transition-colors flex items-center gap-2 ${view === 'list' ? 'bg-[#006cd4]/20 text-[#006cd4]' : 'text-gray-400 hover:text-white'}`}
        >
          <Icon name="list" className="w-4" />
          {t('reservations.viewList')}
        </button>
        <button
          onClick={() => { setWeekFocusDate(null); setView('week') }}
          className={`px-4 py-1.5 rounded-md text-sm transition-colors flex items-center gap-2 ${view === 'week' ? 'bg-[#006cd4]/20 text-[#006cd4]' : 'text-gray-400 hover:text-white'}`}
        >
          <Icon name="calendar-week" className="w-4" />
          {t('reservations.viewWeek')}
        </button>
        <button
          onClick={() => setView('month')}
          className={`px-4 py-1.5 rounded-md text-sm transition-colors flex items-center gap-2 ${view === 'month' ? 'bg-[#006cd4]/20 text-[#006cd4]' : 'text-gray-400 hover:text-white'}`}
        >
          <Icon name="calendar" className="w-4" />
          {t('reservations.viewMonth')}
        </button>
      </div>

      {view === 'week' && <WeekCalendar focusDate={weekFocusDate} />}

      {view === 'month' && (
        <MonthCalendar onJumpToWeek={(iso) => { setWeekFocusDate(iso); setView('week') }} />
      )}

      {view === 'list' && (<>
      {/* Filters */}
      <div className="bg-[#12121a] border border-white/10 rounded-xl p-4 mb-6">
        <div className="flex flex-col lg:flex-row gap-4">
          <input
            type="text"
            placeholder={t('reservations.searchPlaceholder')}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            className="flex-1 px-4 py-2 bg-[#1a1a25] border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#006cd4]"
          />
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(1) }}
            className="px-4 py-2 bg-[#1a1a25] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#006cd4]"
          />
          <input
            type="date"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(1) }}
            className="px-4 py-2 bg-[#1a1a25] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#006cd4]"
          />
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}
            className="px-4 py-2 bg-[#1a1a25] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#006cd4]"
          >
            <option value="">{t('reservations.allStatus')}</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>{t(`reservations.status.${s}`)}</option>
            ))}
          </select>
        </div>
      </div>

      {actionError && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-xl p-4 mb-6">
          {actionError}
        </div>
      )}

      {/* Table */}
      <div className="bg-[#12121a] border border-white/10 rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-400">{t('reservations.loading')}</div>
        ) : reservations.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <p className="text-lg mb-2">{t('reservations.emptyTitle')}</p>
            <p className="text-sm">{t('reservations.emptyDesc')}</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">{t('reservations.reference')}</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">{t('reservations.date')}</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">{t('reservations.customer')}</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">{t('reservations.partySize')}</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">{t('reservations.resource')}</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">{t('common.status')}</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">{t('reservations.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {reservations.map((r) => (
                    <tr key={r.id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="px-4 py-3 text-[#006cd4] font-mono text-sm">{r.reference}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{normalizeDate(r.reservation_date)}</div>
                        <div className="text-sm text-gray-500">{normalizeTime(r.reservation_time)}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{r.customer_name}</div>
                        <div className="text-sm text-gray-500">{r.customer_phone || r.customer_email || '-'}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-300">{r.party_size}</td>
                      <td className="px-4 py-3 text-gray-300">{r.resource_name || '-'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(r.status)}`}>
                          {t(`reservations.status.${r.status}`)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          {r.status === 'pending' && (
                            <button
                              onClick={() => changeStatus(r.id, 'confirmed')}
                              title={t('reservations.confirm')}
                              className="p-2 text-green-400 hover:bg-green-500/10 rounded-lg transition-colors"
                            >
                              <Icon name="check" className="w-4" />
                            </button>
                          )}
                          {(r.status === 'confirmed' || r.status === 'pending') && (
                            <button
                              onClick={() => changeStatus(r.id, 'cancelled')}
                              title={t('reservations.cancel')}
                              className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                            >
                              <Icon name="xmark" className="w-4" />
                            </button>
                          )}
                          {r.status === 'confirmed' && (
                            <>
                              <button
                                onClick={() => changeStatus(r.id, 'completed')}
                                title={t('reservations.complete')}
                                className="p-2 text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                              >
                                <Icon name="circle-check" className="w-4" />
                              </button>
                              <button
                                onClick={() => changeStatus(r.id, 'no_show')}
                                title={t('reservations.noShow')}
                                className="p-2 text-orange-400 hover:bg-orange-500/10 rounded-lg transition-colors"
                              >
                                <Icon name="user-slash" className="w-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-white/10">
              <div className="text-sm text-gray-400">
                {t('common.showingOf', { count: reservations.length, total })}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 bg-white/5 hover:bg-white/10 rounded-lg disabled:opacity-30 transition-colors"
                >
                  {t('common.previous')}
                </button>
                <span className="px-3 py-1 text-sm">
                  {t('common.pageOf', { page, total: totalPages })}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="px-3 py-1 bg-white/5 hover:bg-white/10 rounded-lg disabled:opacity-30 transition-colors"
                >
                  {t('common.next')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      </>)}

      {showCreate && (
        <CreateReservationModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false)
            queryClient.invalidateQueries({ queryKey: ['reservations'] })
          }}
        />
      )}
    </div>
  )
}

// ---------- Manual creation modal ----------

const CreateReservationModal: React.FC<{ onClose: () => void; onCreated: () => void }> = ({ onClose, onCreated }) => {
  const { t } = useTranslation()
  const [form, setForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    reservation_date: '',
    reservation_time: '',
    party_size: 2,
    notes: '',
    force: false,
  })
  const [error, setError] = useState('')
  const [alternatives, setAlternatives] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)

  const set = (key: string, value: any) => setForm((prev) => ({ ...prev, [key]: value }))

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    setError('')
    setAlternatives([])
    try {
      await api.post('/reservations', {
        customer_name: form.customer_name,
        customer_email: form.customer_email || undefined,
        customer_phone: form.customer_phone || undefined,
        reservation_date: form.reservation_date,
        reservation_time: form.reservation_time,
        party_size: form.party_size,
        notes: form.notes || undefined,
        force: form.force,
      })
      onCreated()
    } catch (err: any) {
      const data = err.response?.data
      if (data?.code === 'SLOT_NOT_AVAILABLE') {
        setError(t(`reservations.slotNotAvailable.${data.reason}`, t('reservations.slotNotAvailable.generic')))
        setAlternatives(data.alternativeTimes || [])
      } else {
        setError(data?.error || t('reservations.createError'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  const inputClass = 'w-full px-4 py-2 bg-[#1a1a25] border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#006cd4]'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-[#1a1a25] border border-white/10 rounded-xl p-6 max-w-lg w-full mx-4 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">{t('reservations.newReservation')}</h3>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-white" aria-label={t('common.cancel')}>
            <Icon name="xmark" />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-4 text-left">
        <input required placeholder={t('reservations.form.customerName')} value={form.customer_name} onChange={(e) => set('customer_name', e.target.value)} className={inputClass} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <input type="email" placeholder={t('reservations.form.customerEmail')} value={form.customer_email} onChange={(e) => set('customer_email', e.target.value)} className={inputClass} />
          <input placeholder={t('reservations.form.customerPhone')} value={form.customer_phone} onChange={(e) => set('customer_phone', e.target.value)} className={inputClass} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <input required type="date" value={form.reservation_date} onChange={(e) => set('reservation_date', e.target.value)} className={inputClass} />
          <input required type="time" value={form.reservation_time} onChange={(e) => set('reservation_time', e.target.value)} className={inputClass} />
          <input required type="number" min={1} max={100} value={form.party_size} onChange={(e) => set('party_size', parseInt(e.target.value, 10) || 1)} className={inputClass} />
        </div>
        <textarea placeholder={t('reservations.form.notes')} value={form.notes} onChange={(e) => set('notes', e.target.value)} className={inputClass} rows={2} />

        <label className="flex items-center gap-2 text-sm text-gray-400">
          <input type="checkbox" checked={form.force} onChange={(e) => set('force', e.target.checked)} className="rounded" />
          {t('reservations.form.force')}
        </label>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg p-3 text-sm">
            {error}
            {alternatives.length > 0 && (
              <div className="mt-2 text-gray-300">
                {t('reservations.alternatives')}: {alternatives.join(', ')}
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors">
            {t('common.cancel')}
          </button>
          <button type="submit" disabled={submitting} className="px-4 py-2 bg-[#006cd4] hover:bg-[#006cd4]/80 rounded-lg font-medium transition-colors disabled:opacity-50">
            {submitting ? t('common.saving') : t('common.create')}
          </button>
        </div>
        </form>
      </div>
    </div>
  )
}
